/**
 * useCrowdRoar — synthesizes a low-volume stadium crowd ambient using the
 * Web Audio API. No external audio files, no CORS drama, no licensing issues.
 *
 * Sound design:
 * - Pink noise base (approximates the low murmur of thousands of voices)
 * - Slow LFO on gain to create swells (like a crowd breathing/reacting)
 * - Occasional short "cheer bursts" — brighter noise pulses at random intervals
 * - Overall volume capped low (0.06) so it's ambient background, never
 * louder than the rap verse or judge commentary
 *
 * Autoplay-with-audio is blocked by browsers until user gesture, so the hook
 * exposes { start, stop, running } and consumer components call `start()` from
 * inside a click handler (typically the same "UNMUTE" button that unmutes
 * the video track).
 */
import { useEffect, useRef, useState, useCallback } from "react";

export function useCrowdRoar({ volume = 0.06 } = {}) {
 const ctxRef = useRef(null);
 const nodesRef = useRef(null); // { source, gain, lfo, lfoGain, cheerInterval }
 const [running, setRunning] = useState(false);

 const start = useCallback(async () => {
 if (running) return;
 // Reuse ctx if it exists (browsers cap AudioContext count per tab)
 const AC = window.AudioContext || window.webkitAudioContext;
 if (!AC) return;
 const ctx = ctxRef.current || new AC();
 ctxRef.current = ctx;
 if (ctx.state === "suspended") { try { await ctx.resume(); } catch { /* noop */ } }

 // --- Pink noise generator ---
 // Standard Paul Kellet approximation implemented via ScriptProcessor.
 // 2-second loop so browsers can smoothly repeat it under the reel.
 const buf = ctx.createBuffer(1, ctx.sampleRate * 2, ctx.sampleRate);
 const data = buf.getChannelData(0);
 let b0 = 0, b1 = 0, b2 = 0, b3 = 0, b4 = 0, b5 = 0, b6 = 0;
 for (let i = 0; i < data.length; i++) {
 const white = Math.random() * 2 - 1;
 b0 = 0.99886 * b0 + white * 0.0555179;
 b1 = 0.99332 * b1 + white * 0.0750759;
 b2 = 0.96900 * b2 + white * 0.1538520;
 b3 = 0.86650 * b3 + white * 0.3104856;
 b4 = 0.55000 * b4 + white * 0.5329522;
 b5 = -0.7616 * b5 - white * 0.0168980;
 const pink = (b0 + b1 + b2 + b3 + b4 + b5 + b6 + white * 0.5362) * 0.11;
 b6 = white * 0.115926;
 data[i] = pink;
 }
 const source = ctx.createBufferSource();
 source.buffer = buf;
 source.loop = true;

 // Low-pass filter to soften the noise into a "distant crowd" murmur
 const lp = ctx.createBiquadFilter();
 lp.type = "lowpass";
 lp.frequency.value = 900;
 lp.Q.value = 0.6;

 // Main gain — this is what we ramp for swells
 const gain = ctx.createGain();
 gain.gain.value = volume;

 // LFO for crowd "breathing" swells (0.15 Hz = ~6-second swell cycle)
 const lfo = ctx.createOscillator();
 lfo.frequency.value = 0.15;
 const lfoGain = ctx.createGain();
 lfoGain.gain.value = volume * 0.35;
 lfo.connect(lfoGain).connect(gain.gain);

 source.connect(lp).connect(gain).connect(ctx.destination);
 source.start(0);
 lfo.start(0);

 // Occasional cheer bursts — short, brighter noise pop every 3-8 seconds
 const cheerInterval = setInterval(() => {
 const cheerBuf = ctx.createBuffer(1, ctx.sampleRate * 0.8, ctx.sampleRate);
 const cd = cheerBuf.getChannelData(0);
 for (let i = 0; i < cd.length; i++) {
 // envelope: quick attack, slow decay
 const t = i / cd.length;
 const env = Math.max(0, 1 - t) * (t < 0.05 ? t / 0.05 : 1);
 cd[i] = (Math.random() * 2 - 1) * env * 0.5;
 }
 const csrc = ctx.createBufferSource();
 csrc.buffer = cheerBuf;
 const cbp = ctx.createBiquadFilter();
 cbp.type = "bandpass";
 cbp.frequency.value = 1400;
 cbp.Q.value = 1.2;
 const cg = ctx.createGain();
 cg.gain.value = volume * 0.9;
 csrc.connect(cbp).connect(cg).connect(ctx.destination);
 csrc.start(0);
 csrc.stop(ctx.currentTime + 0.85);
 }, 3000 + Math.random() * 5000);

 nodesRef.current = { source, gain, lfo, lfoGain, cheerInterval };
 setRunning(true);
 }, [running, volume]);

 const stop = useCallback(() => {
 const n = nodesRef.current;
 if (n) {
 try { n.source.stop(); } catch { /* noop */ }
 try { n.lfo.stop(); } catch { /* noop */ }
 clearInterval(n.cheerInterval);
 nodesRef.current = null;
 }
 setRunning(false);
 }, []);

 // Cleanup on unmount
 useEffect(() => () => {
 stop();
 if (ctxRef.current) { try { ctxRef.current.close(); } catch { /* noop */ } }
 }, [stop]);

 return { start, stop, running };
}
